import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { ConfigureSubstitutesModal } from '../components/ConfigureSubstitutesModal';
import { Clock, Package, Calendar, Truck, Lock, Settings, Info, ExternalLink, ChevronRight } from 'lucide-react';
interface SettingsOrderProcessingPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function SettingsOrderProcessingPage({
  currentPage = 'Settings',
  onNavigate
}: SettingsOrderProcessingPageProps) {
  const navigate = useNavigate();
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Modal state
  const [showSubstitutesModal, setShowSubstitutesModal] = useState(false);
  // Section 1: Processing Schedule
  const [processingTime, setProcessingTime] = useState('08:00');
  const [bufferDays, setBufferDays] = useState(2);
  // Section 2: Inventory Management
  const [checkInventory, setCheckInventory] = useState(true);
  const [autoSubstitute, setAutoSubstitute] = useState(false);
  const [reserveInventory, setReserveInventory] = useState(false);
  // Section 3: Subscription Behavior
  const [nextOrderCalculation, setNextOrderCalculation] = useState('last-order');
  const [recalculateFrequency, setRecalculateFrequency] = useState(true);
  const [autoPauseEnabled, setAutoPauseEnabled] = useState(false);
  const [autoPauseCount, setAutoPauseCount] = useState<number | ''>('');
  // Section 4: Shipping Settings
  const [chargeShipping, setChargeShipping] = useState(false);
  const [localPickup, setLocalPickup] = useState(false);
  const [localDelivery, setLocalDelivery] = useState(false);
  // Section 5: Order Merging state
  const [orderMergingEnabled, setOrderMergingEnabled] = useState(false);
  const [mergeOrdersWindow, setMergeOrdersWindow] = useState('3');
  const [mergeOneTimePurchases, setMergeOneTimePurchases] = useState(false);
  // Section 6: Advanced Options
  const [copyNotes, setCopyNotes] = useState(false);
  const [preserveAttributes, setPreserveAttributes] = useState(true);
  const [transferAttributes, setTransferAttributes] = useState(false);
  // Navigation handler
  const handleNavigate = onNavigate || ((page: string) => {
    if (page === 'Dashboard') navigate('/');else if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else if (page === 'Contacts' || page === 'Contact') navigate('/contacts');else if (page === 'Club Builder') navigate('/club-builder');else if (page === 'Products') navigate('/products');else if (page === 'Tools') navigate('/management/tools');else if (page === 'Tag Management' || page === 'Tags') navigate('/settings/tags');else if (page === 'Settings' || page === 'General') navigate('/settings/general');else if (page === 'Order Processing') navigate('/settings/order-processing');else if (page === 'Account and Billing') navigate('/settings/account-billing');else navigate('/');
  });
  // Save handler (simulated)
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  // Toggle handler
  const handleToggle = (setter: React.Dispatch<React.SetStateAction<boolean>>, value: boolean, settingName: string) => {
    setter(value);
    saveSetting();
  };
  // Radio handler
  const handleRadio = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    setter(value);
    saveSetting();
  };
  // Input handler (with debounce simulation)
  const handleInputBlur = () => {
    saveSetting();
  };
  // Handle substitute save
  const handleSaveSubstitutes = (mappings: any) => {
    saveSetting('Substitute settings saved');
  };
  // Generate time options
  const timeOptions = Array.from({
    length: 24
  }, (_, i) => {
    const hour = i;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const value = `${hour.toString().padStart(2, '0')}:00`;
    const label = `${displayHour}:00 ${ampm}`;
    return {
      value,
      label
    };
  });
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-13227" data-mp="2r323yo7halfj">
      {/* NavBar */}
      <div className="mb-6" data-id="element-13228" data-mp="2r07kp007b6np">
        <NavBar tabs={[]} title="Order Processing" data-id="element-13229" data-mp="2w255vzs5wbja" />
      </div>

      {/* Page Header */}
      <div className="mb-8" data-id="element-13230" data-mp="3106rard28mxx">
        <p className="text-[14px] text-[#64748B]" data-id="element-13231" data-mp="30zf7edstsdnh">
          Configure how subscription orders are created and processed
        </p>
      </div>

      {/* Main Content */}
      <div className="space-y-6" data-id="element-13232" data-mp="3ar1crtihfiib">
        {/* Section 1: Processing Schedule */}
        <Card padding="none" data-id="element-13233" data-mp="3es4wir3nhyot">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-13234" data-mp="3eswgt5rss9zs">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-13235" data-mp="3jqog4kuruye0">
              <Clock className="w-5 h-5 text-[#0EA5E9]" data-id="element-13236" data-mp="3jp50mwgxwrda" />
            </div>
            <div data-id="element-13237" data-mp="184nrvqyuco6v">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13238" data-mp="1855quvcw1gih">
                Processing Schedule
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-13239" data-mp="z36dcd130uka">
                Control when subscription orders are created
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-13240" data-mp="u665pxol3jr2">
            {/* Daily Processing Time */}
            <div data-id="element-13241" data-mp="1qtu7v68931oz">
              <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-13242" data-mp="1qwgkvldwu9el">
                Daily Processing Time
              </label>
              <select value={processingTime} onChange={e => {
              setProcessingTime(e.target.value);
              saveSetting();
            }} className="w-full h-[40px] px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-13243" data-mp="1i18vc7v7pr8l">
                {timeOptions.map(option => <option key={option.value} value={option.value} data-id="element-13244" data-mp="25zq7zpg32la4">
                    {option.label}
                  </option>)}
              </select>
              <p className="mt-2 text-[12px] text-[#64748B]" data-id="element-13245" data-mp="1wy0kgupbo6hm">
                Orders scheduled for each day will be created at this time in
                your store's timezone (Australia/Melbourne)
              </p>
            </div>

            {/* Processing Buffer Days */}
            <div data-id="element-13246" data-mp="3vclfaftplr29">
              <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-13247" data-mp="3vb0kcp3hg63v">
                Processing Buffer Days
              </label>
              <div className="flex items-center gap-3" data-id="element-13248" data-mp="1jmlvz7y0ltib">
                <div className="relative w-32" data-id="element-13249" data-mp="1foo6e40c15ee">
                  <input type="number" min="0" max="14" value={bufferDays} onChange={e => setBufferDays(parseInt(e.target.value) || 0)} onBlur={handleInputBlur} className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow" data-id="element-13250" data-mp="1terpim3x0izm" />
                </div>
                <span className="text-[14px] text-[#64748B]" data-id="element-13251" data-mp="1yg7a6d26knhh">days</span>
              </div>
              <p className="mt-2 text-[12px] text-[#64748B]" data-id="element-13252" data-mp="2cbjffedypwcn">
                Number of days before the scheduled date to create the order.
                Gives time for customer notifications and changes.
              </p>
            </div>
          </div>
        </Card>

        {/* Section 2: Inventory Management */}
        <Card padding="none" data-id="element-13253" data-mp="3oa807r0whwmd">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-13254" data-mp="3ob17qu7ooq5k">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-13255" data-mp="3sbl3btclzlpl">
              <Package className="w-5 h-5 text-[#0EA5E9]" data-id="element-13256" data-mp="3sa08e2mdu0r7" />
            </div>
            <div data-id="element-13257" data-mp="ygphzg7hqvq">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13258" data-mp="xxhbagcyguw">
                Inventory Management
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-13259" data-mp="35bdluj5cmy6q">
                Control how inventory affects subscription orders
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-13260" data-mp="39f1u741t1xt1">
            {/* Toggle 1: Check Inventory */}
            <div className="flex items-start justify-between" data-id="element-13261" data-mp="3eaqvpcxdxorz">
              <div className="flex-1 pr-6" data-id="element-13262" data-mp="3jc6gd3vnht9u">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13263" data-mp="3j9255mjrpx9o">
                  Check Inventory Before Processing
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13264" data-mp="yjuwaxvj4423">
                  Verify product availability before creating subscription
                  orders. Orders with out-of-stock items will be held.
                </p>
              </div>
              <button onClick={() => handleToggle(setCheckInventory, !checkInventory, 'Check Inventory')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${checkInventory ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={checkInventory} data-id="element-13265" data-mp="2e5yhpkys7owx">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checkInventory ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13266" data-mp="1vbszqtfjet2n" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-13267" data-mp="1lh11dne4s26t" />

            {/* Toggle 2: Auto-substitute */}
            <div className="flex items-start justify-between" data-id="element-13268" data-mp="3krsfwuhkpirs">
              <div className="flex-1 pr-6" data-id="element-13269" data-mp="3fszc5ts3964t">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13270" data-mp="3fr4o46lisxwi">
                  Auto-substitute Out of Stock Products
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13271" data-mp="1o60pulnz5nx2">
                  Automatically replace out-of-stock products with designated
                  alternatives
                </p>
                {autoSubstitute && <button onClick={() => setShowSubstitutesModal(true)} className="mt-2 inline-flex items-center gap-1 text-[14px] text-[#0EA5E9] hover:underline font-medium" data-id="element-13272" data-mp="1y4gl90u1bjcd">
                    Configure Substitutes
                    <ExternalLink className="w-3 h-3" data-id="element-13273" data-mp="15gazqbv5pqbh" />
                  </button>}
              </div>
              <button onClick={() => handleToggle(setAutoSubstitute, !autoSubstitute, 'Auto-substitute')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${autoSubstitute ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={autoSubstitute} data-id="element-13274" data-mp="1k572bteg2b4p">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoSubstitute ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13275" data-mp="2yak8v98rll6g" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-13276" data-mp="2oeqoj3ot0ne4" />

            {/* Toggle 3: Reserve Inventory */}
            <div className="flex items-start justify-between" data-id="element-13277" data-mp="2tdacf9myk994">
              <div className="flex-1 pr-6" data-id="element-13278" data-mp="3h2cu2hhle23m">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13279" data-mp="3h2mku7agodad">
                  Reserve Inventory for Upcoming Orders
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13280" data-mp="381y4v71ic2uu">
                  Reserve stock for subscription orders 7 days before processing
                </p>
              </div>
              <button onClick={() => handleToggle(setReserveInventory, !reserveInventory, 'Reserve Inventory')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${reserveInventory ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={reserveInventory} data-id="element-13281" data-mp="1wm9hiekcd1ti">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${reserveInventory ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13282" data-mp="28dra9ex7lb7" />
              </button>
            </div>
          </div>
        </Card>

        {/* Section 3: Subscription Behavior */}
        <Card padding="none" data-id="element-13283" data-mp="3klby90a4reev">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-13284" data-mp="3fkxqljcfy6hd">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-13285" data-mp="3fnaf6fcpff3e">
              <Calendar className="w-5 h-5 text-[#0EA5E9]" data-id="element-13286" data-mp="3ammcuxj0zbtv" />
            </div>
            <div data-id="element-13287" data-mp="35njjwbgbkw29">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13288" data-mp="35o1njt5back7">
                Subscription Behavior
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-13289" data-mp="18sdn9lu80brb">
                Define how subscriptions calculate dates and handle changes
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-13290" data-mp="1cc5fhodxtovd">
            {/* Next Order Date Calculation */}
            <div data-id="element-13291" data-mp="1gcfxjzf085ql">
              <label className="block text-[14px] font-medium text-[#475569] mb-3" data-id="element-13292" data-mp="1gbwbc6ibmv6r">
                When frequency changes, calculate next order from:
              </label>
              <div className="space-y-4" data-id="element-13293" data-mp="1q2hbhydn87rs">
                {[{
                id: 'today',
                label: "Today's date",
                desc: 'Next order scheduled from the date of the change'
              }, {
                id: 'last-order',
                label: 'Last order date',
                desc: 'Next order scheduled from when the last order was placed'
              }, {
                id: 'billing-date',
                label: 'Current billing date',
                desc: 'Keep the existing billing date, apply new frequency going forward'
              }].map(option => <div key={option.id} className="flex items-start cursor-pointer" onClick={() => handleRadio(setNextOrderCalculation, option.id)} data-id="element-13294" data-mp="2ireszza160jh">
                    <div className={`mt-0.5 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${nextOrderCalculation === option.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-13295" data-mp="3rr94et7d14rs">
                      {nextOrderCalculation === option.id && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-13296" data-mp="3vq87s9dg0th4" />}
                    </div>
                    <div className="ml-3" data-id="element-13297" data-mp="ehl8vo8dc2hz">
                      <span className="block text-[14px] font-medium text-[#0F172A]" data-id="element-13298" data-mp="egjo2s1jw1n2">
                        {option.label}
                      </span>
                      <span className="block text-[13px] text-[#64748B] mt-0.5" data-id="element-13299" data-mp="n9wn8bq7jzhl">
                        {option.desc}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-13300" data-mp="x79qkfn7lmfe" />

            {/* Recalculate on Frequency Change */}
            <div className="flex items-start justify-between" data-id="element-13301" data-mp="s8g9kd8prdah">
              <div className="flex-1 pr-6" data-id="element-13302" data-mp="1bxe1nabxeqig">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13303" data-mp="1gv5ty5ghgen6">
                  Recalculate on Frequency Change
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13304" data-mp="11x3o1olzbh2r">
                  Automatically recalculate the next charge date when a customer
                  changes their delivery frequency
                </p>
              </div>
              <button onClick={() => handleToggle(setRecalculateFrequency, !recalculateFrequency, 'Recalculate Frequency')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${recalculateFrequency ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={recalculateFrequency} data-id="element-13305" data-mp="t3i9o6dq81tv">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${recalculateFrequency ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13306" data-mp="a93aafni675i" />
              </button>
            </div>
          </div>
        </Card>

        {/* Section 4: Shipping Settings */}
        <Card padding="none" data-id="element-13307" data-mp="2c26fn2aj4qdi">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-13308" data-mp="274wtbvifsutw">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-13309" data-mp="272trme7x9d9c">
              <Truck className="w-5 h-5 text-[#0EA5E9]" data-id="element-13310" data-mp="227eg3sxdztg3" />
            </div>
            <div data-id="element-13311" data-mp="2zu3qor65nedk">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13312" data-mp="2zsixb4vku1ha">
                Shipping Settings
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-13313" data-mp="2uvk72xf1aeod">
                Configure shipping for subscription orders
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-13314" data-mp="2ltattzsds77a">
            {/* Toggle 1: Charge Shipping */}
            <div className="flex items-start justify-between" data-id="element-13315" data-mp="1ehk6dk8d6l02">
              <div className="flex-1 pr-6" data-id="element-13316" data-mp="1aexb19chihy4">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13317" data-mp="1ae4cuwrld0rl">
                  Charge Shipping on Recurring Orders
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13318" data-mp="3q3ugpqm7yi7m">
                  Apply shipping charges to recurring subscription orders. Most
                  wine clubs offer free shipping for members.
                </p>
              </div>
              <button onClick={() => handleToggle(setChargeShipping, !chargeShipping, 'Charge Shipping')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${chargeShipping ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={chargeShipping} data-id="element-13319" data-mp="3pba4g3hk7bp">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${chargeShipping ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13320" data-mp="mnnkg564vn8v" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-13321" data-mp="2odpd060lbf27" />

            {/* Toggle 2: Local Pickup */}
            <div className="flex items-start justify-between" data-id="element-13322" data-mp="u653b1zwgeei">
              <div className="flex-1 pr-6" data-id="element-13323" data-mp="u4afnfx8u86q">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13324" data-mp="q409a25jhfrc">
                  Enable Local Pickup
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13325" data-mp="13vonqc39ggbh">
                  Allow customers to select local pickup instead of delivery for
                  their subscription orders
                </p>
                {localPickup && <button className="mt-2 inline-flex items-center gap-1 text-[14px] text-[#0EA5E9] hover:underline font-medium" data-id="element-13326" data-mp="7675kxn3d1sw">
                    Configure Pickup Locations
                    <ExternalLink className="w-3 h-3" data-id="element-13327" data-mp="l79rnae36c3t" />
                  </button>}
              </div>
              <button onClick={() => handleToggle(setLocalPickup, !localPickup, 'Local Pickup')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${localPickup ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={localPickup} data-id="element-13328" data-mp="g8ykj7f6efpa">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${localPickup ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13329" data-mp="3ejtfl5nh6jhy" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-13330" data-mp="35j3rko4ghxes" />

            {/* Toggle 3: Local Delivery */}
            <div className="flex items-start justify-between" data-id="element-13331" data-mp="3t8pot619q2k5">
              <div className="flex-1 pr-6" data-id="element-13332" data-mp="13m3474thoj8">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13333" data-mp="12kh57m9jhmq">
                  Enable Local Delivery
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13334" data-mp="3jg0n32vis320">
                  Offer local delivery option for customers within your delivery
                  zone
                </p>
                {localDelivery && <button className="mt-2 inline-flex items-center gap-1 text-[14px] text-[#0EA5E9] hover:underline font-medium" data-id="element-13335" data-mp="fza5rruhm3l0">
                    Configure Delivery Zones
                    <ExternalLink className="w-3 h-3" data-id="element-13336" data-mp="11240mlpwk3wr" />
                  </button>}
              </div>
              <button onClick={() => handleToggle(setLocalDelivery, !localDelivery, 'Local Delivery')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${localDelivery ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={localDelivery} data-id="element-13337" data-mp="15zne3jes0cnn">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${localDelivery ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13338" data-mp="naqlrl2xvu82" />
              </button>
            </div>
          </div>
        </Card>

        {/* Section 5: Order Merging */}
        <Card padding="none" data-id="element-13339" data-mp="23nnpc8lp0p80">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-13340" data-mp="23op4ao5txvv0">
            <div className="flex items-center gap-3" data-id="element-13341" data-mp="27ohhxfa2ys78">
              <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-13342" data-mp="3s7jvifdfs9xm">
                <Package className="w-5 h-5 text-[#0EA5E9]" data-id="element-13343" data-mp="3s832x25gnfxe" />
              </div>
              <div data-id="element-13344" data-mp="3o7j47u58o495">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13345" data-mp="3o82o3geg92pt">
                  Order Merging
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-13346" data-mp="11ihpz23728a">
                  Combine subscription orders with one-time purchases to save on
                  shipping
                </p>
              </div>
            </div>
            <button onClick={() => handleToggle(setOrderMergingEnabled, !orderMergingEnabled, 'Order Merging')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${orderMergingEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={orderMergingEnabled} data-id="element-13347" data-mp="as4px87h4qgy">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${orderMergingEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13348" data-mp="2ljwjn6lm6vl9" />
            </button>
          </div>

          {orderMergingEnabled && <div className="p-6 space-y-6" data-id="element-13349" data-mp="ug4f2bpdlmdt">
              {/* Merge Orders Within */}
              <div data-id="element-13350" data-mp="zdctq2bbf8ab">
                <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-13351" data-mp="zcutyvpg2bxn">
                  Merge orders within
                </label>
                <select value={mergeOrdersWindow} onChange={e => {
              setMergeOrdersWindow(e.target.value);
              saveSetting();
            }} className="w-full h-[40px] px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-13352" data-mp="18fduva9o6eh9">
                  <option value="1" data-id="element-13353" data-mp="37mkpzb0ce1ou">1 day</option>
                  <option value="3" data-id="element-13354" data-mp="32n7uk1venman">3 days</option>
                  <option value="5" data-id="element-13355" data-mp="32oj0a78ev44e">5 days</option>
                  <option value="7" data-id="element-13356" data-mp="38cj88n90odr">7 days</option>
                  <option value="14" data-id="element-13357" data-mp="371f27pw5em4">14 days</option>
                </select>
                <p className="mt-2 text-[12px] text-[#64748B]" data-id="element-13358" data-mp="3vcwwchfxvqdb">
                  One-time purchases placed within this window of a subscription
                  order will be combined into a single shipment
                </p>
              </div>
            </div>}
        </Card>

        {/* Section 6: Advanced Options */}
        <Card padding="none" data-id="element-13359" data-mp="mx4uw3u3bqte">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-13360" data-mp="i53m4rw4si84">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-13361" data-mp="i38yv6xe0e0v">
              <Settings className="w-5 h-5 text-[#0EA5E9]" data-id="element-13362" data-mp="1ot2yv5ib38r" />
            </div>
            <div data-id="element-13363" data-mp="6p737r0v98w8">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13364" data-mp="6pqmbb2963bu">
                Advanced Options
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-13365" data-mp="3ow4uetdubj2b">
                Additional order processing settings
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-13366" data-mp="lempatpgcq5l">
            {/* Toggle 1: Copy Order Notes */}
            <div className="flex items-start justify-between" data-id="element-13367" data-mp="pfey5luewt1c">
              <div className="flex-1 pr-6" data-id="element-13368" data-mp="pgzthdojwg09">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13369" data-mp="bmpdbkqmdu5t">
                  Copy Order Notes to Subscription
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13370" data-mp="gkiiqbk3mk51">
                  Copy notes from the original order to the subscription record
                </p>
              </div>
              <button onClick={() => handleToggle(setCopyNotes, !copyNotes, 'Copy Order Notes')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${copyNotes ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={copyNotes} data-id="element-13371" data-mp="194h25b9ruepu">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${copyNotes ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13372" data-mp="1n8en2wyrmqya" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-13373" data-mp="via7dblyas3e" />

            {/* Toggle 2: Preserve Attributes */}
            <div className="flex items-start justify-between" data-id="element-13374" data-mp="19hjihwvauw0i">
              <div className="flex-1 pr-6" data-id="element-13375" data-mp="14i6n2nqd4gmb">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13376" data-mp="14h5f4s4n8a8t">
                  Preserve Line Item Attributes
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13377" data-mp="2ky9how1b2nkm">
                  Keep custom product attributes (like gift messages) on
                  recurring orders
                </p>
              </div>
              <button onClick={() => handleToggle(setPreserveAttributes, !preserveAttributes, 'Preserve Attributes')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${preserveAttributes ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={preserveAttributes} data-id="element-13378" data-mp="2rzmt24dxwhkg">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preserveAttributes ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13379" data-mp="299566gfsgg70" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-13380" data-mp="2iyoqzrn2ak4i" />

            {/* Toggle 3: Transfer Attributes */}
            <div className="flex items-start justify-between" data-id="element-13381" data-mp="1lju9pb5h17yo">
              <div className="flex-1 pr-6" data-id="element-13382" data-mp="1lhgk3htk8nyi">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13383" data-mp="1qi4bi4lpb6t9">
                  Transfer Order Attributes
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-13384" data-mp="1iyd5hdmmervr">
                  Copy order-level attributes to each recurring order
                </p>
              </div>
              <button onClick={() => handleToggle(setTransferAttributes, !transferAttributes, 'Transfer Attributes')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${transferAttributes ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={transferAttributes} data-id="element-13385" data-mp="18zxedanlfim9">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${transferAttributes ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13386" data-mp="qbioiqp0ian1" />
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Configure Substitutes Modal */}
      <ConfigureSubstitutesModal isOpen={showSubstitutesModal} onClose={() => setShowSubstitutesModal(false)} onSave={handleSaveSubstitutes} data-id="element-13387" data-mp="cag047ajoo8y" />

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-13388" data-mp="2570fchk0ghhq">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-13389" data-mp="1q9zmubtzwii7">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-13390" data-mp="1v6q0ytm8p40z" />
            <div className="flex-1" data-id="element-13391" data-mp="1hgcxp4kamdhz">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-13392" data-mp="1hfbf8f0y6oq8">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-13393" data-mp="1m9peisiqdg06">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}